/**
 * One collapsible sidebar section (FEED_SPEC § Filter sidebar).
 *
 * The count chip is the clear button *in whole* — count and ⓧ alike — and a **sibling** of the
 * toggle, not a child: a `<button>` cannot nest, and clicking it must clear the section without
 * also collapsing it. It is visible open or closed, so a collapsed section can never hide an
 * active filter. Hover lives on the row so the chip sits inside the highlight rather than beside
 * it.
 */

import { useId, type ReactNode } from 'react';
import { ChevronDownIcon, CloseIcon, cx } from '../../components';

export type SidebarSectionProps = {
  title: string;
  open: boolean;
  onToggle: () => void;
  /** Active choices in this section; `0` renders no chip. */
  count: number;
  onClear: () => void;
  children: ReactNode;
};

export function SidebarSection({
  title,
  open,
  onToggle,
  count,
  onClear,
  children,
}: SidebarSectionProps) {
  const bodyId = useId();

  return (
    <section className="sb-section">
      <div className="sb-section__row">
        <button
          type="button"
          className="sb-section__toggle"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={onToggle}
        >
          <ChevronDownIcon size={12} className={cx('sb-section__caret', !open && 'is-collapsed')} />
          <span className="sb-section__title">{title}</span>
        </button>
        {count > 0 ? (
          <button
            type="button"
            className="sb-chip"
            aria-label={`Clear ${title} filter`}
            onClick={onClear}
          >
            {count}
            <CloseIcon size={10} />
          </button>
        ) : null}
      </div>
      <div id={bodyId} className="sb-section__body" hidden={!open}>
        {children}
      </div>
    </section>
  );
}

export default SidebarSection;
